import type { WidgetCoreDefinition, TeXMLContext, TeXMLElement, ValidationResult } from './types';

export const playAudioSources = ['catalog', 'custom_url'] as const;
export type PlayAudioSource = (typeof playAudioSources)[number];

export type PlayConfig = {
  source?: PlayAudioSource;
  audioFileId?: string;
  audioFileName?: string;
  audioUrl?: string;
  customUrl?: string;
  loop: number;
};

function normalizeValue(value: string | undefined): string {
  return value?.trim() ?? '';
}

export function getPlayAudioSource(config: PlayConfig): PlayAudioSource {
  if (config.source === 'catalog' || config.source === 'custom_url') {
    return config.source;
  }

  return normalizeValue(config.audioFileId) ? 'catalog' : 'custom_url';
}

export function getPlayCustomUrl(config: PlayConfig): string {
  const customUrl = normalizeValue(config.customUrl);
  if (customUrl) {
    return customUrl;
  }

  return getPlayAudioSource(config) === 'custom_url' ? normalizeValue(config.audioUrl) : '';
}

export function getPlayAudioUrl(config: PlayConfig): string {
  if (getPlayAudioSource(config) === 'custom_url') {
    return getPlayCustomUrl(config);
  }

  return normalizeValue(config.audioUrl);
}

export function hasPlayAudioSelection(config: PlayConfig): boolean {
  if (getPlayAudioSource(config) === 'catalog') {
    return !!normalizeValue(config.audioFileId);
  }

  return !!getPlayCustomUrl(config);
}

export function getPlayPreviewLabel(config: PlayConfig): string {
  if (getPlayAudioSource(config) === 'catalog') {
    const audioFileName = normalizeValue(config.audioFileName);
    if (audioFileName) {
      return audioFileName;
    }
  }

  const audioUrl = getPlayAudioSource(config) === 'custom_url' ? getPlayCustomUrl(config) : getPlayAudioUrl(config);
  return audioUrl ? audioUrl.split('/').pop() || 'Audio file' : 'No audio configured';
}

function generateTeXML(config: PlayConfig, _context: TeXMLContext): TeXMLElement[] {
  const audioUrl = getPlayAudioUrl(config);
  if (!audioUrl) {
    return [];
  }

  return [
    {
      tag: 'Play',
      attributes: {
        loop: config.loop > 1 ? config.loop.toString() : undefined,
      },
      content: audioUrl,
    },
  ];
}

function validateConfig(config: PlayConfig): ValidationResult {
  const errors: string[] = [];
  const source = getPlayAudioSource(config);
  const audioUrl = getPlayAudioUrl(config);

  if (source === 'catalog') {
    if (!normalizeValue(config.audioFileId)) {
      errors.push('Audio media is required');
    }
  } else {
    if (!audioUrl) {
      errors.push('Custom audio URL is required');
    }

    try {
      new URL(audioUrl);
    } catch {
      if (audioUrl) {
        errors.push('Custom audio URL must be a valid URL');
      }
    }
  }

  if (config.loop < 1 || config.loop > 100) {
    errors.push('Loop must be between 1 and 100');
  }

  return { valid: errors.length === 0, errors };
}

export const playWidgetCore: WidgetCoreDefinition<PlayConfig> = {
  id: 'play',
  version: 1,
  name: 'Play',
  description: 'Play uploaded audio media or a custom URL to the caller',
  category: 'voice',
  tier: 'standard',

  defaultConfig: {
    source: 'catalog',
    audioFileId: '',
    audioFileName: '',
    audioUrl: '',
    customUrl: '',
    loop: 1,
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: false,
  },

  validation: {
    validateConfig,
  },
};
